import { useState, useEffect, useCallback } from 'react';
import { getAdminProducts, getAllProductIds, updateAdminProduct, deactivateProduct, getCategories, getAdminSuppliers, bulkProductAction } from '@/lib/api';
import { Search, Lock, Unlock, Edit2, Trash2, X, Check, CheckSquare, Square, Tag, MessageSquare, Percent, Truck, LayoutGrid, Filter } from 'lucide-react';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [allDbSelected, setAllDbSelected] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSalePercent, setBulkSalePercent] = useState('10');
  const [bulkSupplierId, setBulkSupplierId] = useState('');
  const [bulkCategorySlug, setBulkCategorySlug] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const [showBulkPanel, setShowBulkPanel] = useState(false);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (search) params.search = search;
    if (catFilter) params.category = catFilter;
    getAdminProducts(params).then(r => {
      setProducts(r.data.products);
      setTotal(r.data.total);
      setPages(r.data.pages);
      setLoading(false);
    });
  }, [page, search, catFilter]);

  useEffect(() => { getCategories().then(r => setCategories(r.data)); getAdminSuppliers().then(r => setSuppliers(r.data)); }, []);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggleSelect = (id) => {
    setAllDbSelected(false);
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectPage = () => {
    setAllDbSelected(false);
    if (pageAllSelected) {
      // Deselect page products
      const next = new Set(selected);
      products.forEach(p => next.delete(p.id));
      setSelected(next);
    } else {
      // Select page products
      const next = new Set(selected);
      products.forEach(p => next.add(p.id));
      setSelected(next);
    }
  };

  const selectAllDatabase = async () => {
    const params = {};
    if (search) params.search = search;
    if (catFilter) params.category = catFilter;
    try {
      const res = await getAllProductIds(params);
      setSelected(new Set(res.data.ids));
      setAllDbSelected(true);
    } catch {}
  };

  const clearSelection = () => {
    setSelected(new Set());
    setAllDbSelected(false);
  };

  const handleBulkAction = async () => {
    if (selected.size === 0) return;
    setBulkMsg('');
    const data = { product_ids: Array.from(selected), action: bulkAction };
    if (bulkAction === 'sale') data.sale_percent = parseFloat(bulkSalePercent) || 0;
    if (bulkAction === 'tag' || bulkAction === 'note') data.value = bulkValue;
    if (bulkAction === 'change_supplier') data.supplier_id = bulkSupplierId;
    if (bulkAction === 'change_category') data.category_slug = bulkCategorySlug;
    try {
      const res = await bulkProductAction(data);
      setBulkMsg(res.data.message);
      setSelected(new Set());
      setAllDbSelected(false);
      setBulkAction('');
      fetchProducts();
    } catch (err) {
      setBulkMsg(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setEditForm({ supplier_price: p.supplier_price, public_price: p.public_price, is_price_frozen: p.is_price_frozen, delivery_delay: p.delivery_delay, tag: p.tag || '', note: p.note || '', sale_percent: p.sale_percent || 0 });
  };

  const handleSave = async (id) => {
    await updateAdminProduct(id, editForm);
    setEditing(null);
    fetchProducts();
  };

  const toggleFreeze = async (p) => {
    await updateAdminProduct(p.id, { is_price_frozen: !p.is_price_frozen });
    fetchProducts();
  };

  const pageAllSelected = products.length > 0 && products.every(p => selected.has(p.id));

  return (
    <div data-testid="admin-products" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold text-[#111] tracking-tight">Gestion des produits</h1>
        {selected.size > 0 && (
          <button onClick={() => setShowBulkPanel(!showBulkPanel)} className="flex items-center gap-2 px-4 py-2 bg-[#111] text-white text-sm font-semibold rounded-sm">
            <CheckSquare size={14} /> {selected.size} sélectionné{selected.size > 1 ? 's' : ''} — Actions
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
          <input
            type="text" placeholder="Rechercher par nom..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="admin-product-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none"
          />
        </div>
        <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }} className="px-3 py-2 text-sm border border-black/15 bg-white">
          <option value="">Toutes catégories</option>
          {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <span className="text-sm text-black/40 self-center">{total} produit{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk Actions Panel */}
      {showBulkPanel && selected.size > 0 && (
        <div className="border-2 border-[#00A5FF] bg-[#00A5FF]/5 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">{selected.size} produit{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}</h3>
            <button onClick={() => { setShowBulkPanel(false); setSelected(new Set()); }} className="text-black/30 hover:text-black"><X size={16} /></button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-black/50 block mb-1">Action</label>
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="px-3 py-2 text-sm border border-black/15 bg-white min-w-[200px]">
                <option value="">Choisir une action...</option>
                <option value="delete">Désactiver (masquer du catalogue)</option>
                <option value="delete_permanent">Supprimer définitivement</option>
                <option value="sale">Appliquer une réduction (%)</option>
                <option value="remove_sale">Retirer la réduction</option>
                <option value="tag">Appliquer un tag</option>
                <option value="note">Ajouter une note</option>
                <option value="change_supplier">Changer le fournisseur</option>
                <option value="change_category">Changer la catégorie</option>
              </select>
            </div>
            {bulkAction === 'sale' && (
              <div>
                <label className="text-xs text-black/50 block mb-1">Réduction %</label>
                <input type="number" value={bulkSalePercent} onChange={(e) => setBulkSalePercent(e.target.value)} className="w-20 px-3 py-2 text-sm border border-black/15" min="0" max="90" />
              </div>
            )}
            {(bulkAction === 'tag' || bulkAction === 'note') && (
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-black/50 block mb-1">{bulkAction === 'tag' ? 'Tag' : 'Note'}</label>
                <input type="text" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder={bulkAction === 'tag' ? 'Ex: Nouveau, Promo, Bio...' : 'Commentaire visible sur la fiche...'} className="w-full px-3 py-2 text-sm border border-black/15" />
              </div>
            )}
            {bulkAction === 'change_supplier' && (
              <div>
                <label className="text-xs text-black/50 block mb-1">Fournisseur</label>
                <select value={bulkSupplierId} onChange={(e) => setBulkSupplierId(e.target.value)} className="px-3 py-2 text-sm border border-black/15 bg-white">
                  <option value="">Choisir...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            {bulkAction === 'change_category' && (
              <div>
                <label className="text-xs text-black/50 block mb-1">Catégorie</label>
                <select value={bulkCategorySlug} onChange={(e) => setBulkCategorySlug(e.target.value)} className="px-3 py-2 text-sm border border-black/15 bg-white">
                  <option value="">Choisir...</option>
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
            )}
            {bulkAction && (
              <button onClick={() => {
                if (bulkAction === 'delete_permanent' && !window.confirm(`ATTENTION : Supprimer définitivement ${selected.size} produit(s) ? Cette action est irréversible.`)) return;
                handleBulkAction();
              }} className={`px-4 py-2 text-sm font-semibold rounded-sm text-white ${bulkAction === 'delete' || bulkAction === 'delete_permanent' ? 'bg-[#E8231B] hover:bg-[#c91d16]' : 'bg-[#00A5FF] hover:bg-[#0090e0]'}`}>
                {bulkAction === 'delete_permanent' ? 'Supprimer définitivement' : 'Appliquer'}
              </button>
            )}
          </div>
          {bulkMsg && <p className="text-sm text-green-600 mt-2">{bulkMsg}</p>}
        </div>
      )}

      {/* Table */}
      <div className="border border-black/10 overflow-x-auto">
        {/* Select all DB banner */}
        {pageAllSelected && !allDbSelected && total > products.length && (
          <div className="bg-[#00A5FF]/5 border-b border-[#00A5FF]/20 px-4 py-2 flex items-center justify-center gap-2 text-sm">
            <span className="text-black/60">Les {products.length} produits de cette page sont sélectionnés.</span>
            <button onClick={selectAllDatabase} className="text-[#00A5FF] font-bold hover:underline">
              TOUS LES PRODUITS ({total})
            </button>
          </div>
        )}
        {allDbSelected && (
          <div className="bg-[#00A5FF]/10 border-b border-[#00A5FF]/30 px-4 py-2 flex items-center justify-center gap-2 text-sm">
            <CheckSquare size={14} className="text-[#00A5FF]" />
            <span className="font-bold text-[#00A5FF]">TOUS LES PRODUITS ({selected.size}) sélectionnés</span>
            <button onClick={clearSelection} className="text-black/40 hover:text-black/60 ml-2 text-xs underline">
              Annuler la sélection
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50 border-b border-black/10">
              <th className="w-10 px-2 py-2 text-center">
                <button onClick={toggleSelectPage} className="text-black/30 hover:text-[#00A5FF]">
                  {pageAllSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
              </th>
              <th className="text-left px-2 py-2 font-semibold">SKU</th>
              <th className="text-left px-2 py-2 font-semibold">Nom</th>
              <th className="text-left px-2 py-2 font-semibold">Catégorie</th>
              <th className="text-right px-2 py-2 font-semibold">Fourn.</th>
              <th className="text-right px-2 py-2 font-semibold">Public</th>
              <th className="text-center px-2 py-2 font-semibold">Solde</th>
              <th className="text-center px-2 py-2 font-semibold">Gelé</th>
              <th className="text-left px-2 py-2 font-semibold">Tag</th>
              <th className="text-right px-2 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className={`border-t border-black/5 hover:bg-[#F8F9FA] transition-colors duration-200 ${!p.is_active ? 'opacity-30' : ''} ${selected.has(p.id) ? 'bg-[#00A5FF]/5' : ''}`}>
                <td className="px-2 py-2 text-center">
                  <button onClick={() => toggleSelect(p.id)} className={selected.has(p.id) ? 'text-[#00A5FF]' : 'text-black/20 hover:text-black/40'}>
                    {selected.has(p.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </td>
                <td className="px-2 py-2 text-xs font-mono text-black/50">{p.sku}</td>
                <td className="px-2 py-2 font-medium max-w-[180px] truncate">{p.name}</td>
                <td className="px-2 py-2 text-xs text-black/60">{p.category}</td>
                <td className="px-2 py-2 text-right">
                  {editing === p.id ? (
                    <input type="number" value={editForm.supplier_price} onChange={(e) => setEditForm({...editForm, supplier_price: parseFloat(e.target.value)})} className="w-20 px-1 py-0.5 text-right border border-black/15 text-xs" step="0.01" />
                  ) : (
                    <span className="text-xs text-black/40">{p.supplier_price?.toFixed(2)}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right font-bold">
                  {editing === p.id ? (
                    <input type="number" value={editForm.public_price} onChange={(e) => setEditForm({...editForm, public_price: parseFloat(e.target.value)})} className="w-20 px-1 py-0.5 text-right border border-black/15 text-xs" step="0.01" />
                  ) : (
                    <span>{p.public_price?.toFixed(2)}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  {p.sale_percent > 0 && (
                    <span className="text-xs font-bold text-[#E8231B] bg-[#E8231B]/10 px-1.5 py-0.5">-{p.sale_percent}%</span>
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  <button onClick={() => toggleFreeze(p)} className={`p-1 ${p.is_price_frozen ? 'text-[#E8231B]' : 'text-black/15 hover:text-black/40'}`}>
                    {p.is_price_frozen ? <Lock size={14} /> : <Unlock size={14} />}
                  </button>
                </td>
                <td className="px-2 py-2">
                  {p.tag && <span className="text-xs px-1.5 py-0.5 bg-[#F5C400]/15 text-[#111] font-medium">{p.tag}</span>}
                </td>
                <td className="px-2 py-2 text-right">
                  {editing === p.id ? (
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleSave(p.id)} className="p-1 text-green-600"><Check size={14} /></button>
                      <button onClick={() => setEditing(null)} className="p-1 text-black/30"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(p)} className="p-1 text-[#00A5FF] hover:bg-[#00A5FF]/10"><Edit2 size={14} /></button>
                      <button onClick={async () => { if (window.confirm('Désactiver ?')) { await deactivateProduct(p.id); fetchProducts(); }}} className="p-1 text-black/15 hover:text-[#E8231B]"><Trash2 size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {[...Array(Math.min(pages, 10))].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)} className={`w-8 h-8 text-xs font-medium border ${page === i + 1 ? 'bg-[#00A5FF] text-white border-[#00A5FF]' : 'border-black/15'}`}>{i + 1}</button>
          ))}
          {pages > 10 && <span className="text-xs text-black/40">...{pages}</span>}
        </div>
      )}
    </div>
  );
}
